import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ClipboardCheck, Minus, PencilLine, Plus } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type EstimateReferenceCandidate,
  type PlanField,
  type StoryPointAnchor,
  MIN_CALIBRATION_SAMPLES,
  REFERENCE_RATIO_STOPS,
  REFERENCE_RATIO_VALUES,
  STORY_POINT_SCALE,
  convertedStoryPoints,
  hoursFromRates,
  estimateFromReference,
  ratioIndexOf,
  ratioLabel,
  toStoryPointScale,
  scaleConvertedPoints,
} from '@apm/contracts';
import { bucketOf } from '@apm/estimate';
import { Button } from '../../../components/ui/button';
import { DatePicker } from '../../../components/ui/date-picker';
import { errorMessage } from '../../../lib/error-message';
import { todayIso } from '../../../lib/overdue';
import { cn } from '../../../lib/utils';
import { taskFormApi } from '../api';

export interface PlanGateTask {
  readonly id: string;
  readonly title: string;
  readonly missing: readonly PlanField[];
  /** Đặt được hạn không (`TaskView.canSchedule`). Thiếu = để server quyết. */
  readonly canSchedule?: boolean | undefined;
  /** Tên người làm cho tiêu đề phần 1 ở chế độ `estimate`. Thiếu/`null` = "bạn". */
  readonly ownerName?: string | null | undefined;
  /** Điền sẵn ô "Tự điền số SP" — ước lượng lại thì bắt đầu từ số đang có. */
  readonly estimateHours?: number | null | undefined;
  readonly storyPoints?: number | null | undefined;
}

/**
 * SP làm mốc của một việc mẫu — APM-SPA-e. SP thật thì dùng nguyên; chưa có SP thì
 * quy giờ thật ra SP theo neo (`convertedStoryPoints`) và đánh dấu `converted` để
 * giao diện không lẫn với SP thật. Không có neo (API cũ) ⇒ `null`.
 */
export function basePointsOf(
  candidate: Pick<EstimateReferenceCandidate, 'storyPoints' | 'actualHours' | 'doerLevel'>,
  anchor: StoryPointAnchor | undefined,
): { readonly value: number; readonly converted: boolean } | null {
  if (candidate.storyPoints !== null) return { value: candidate.storyPoints, converted: false };
  if (!anchor) return null;
  const value = convertedStoryPoints({
    hours: candidate.actualHours,
    doerLevel: candidate.doerLevel ?? null,
    bucket: bucketOf(candidate.actualHours),
    anchor,
  });
  return value === null ? null : { value, converted: true };
}

/** Số thập phân theo ngôn ngữ giao diện: `3,2` ở tiếng Việt, `3.2` ở tiếng Anh. */
function decimal(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value);
}

/** Giá trị của `chosen` khi người dùng chọn "Tự điền số SP" thay cho một việc mẫu. */
const MANUAL = 'manual';

/** `+25%`, `−25%`, `0%`. */
function signed(pct: number): string {
  if (pct > 0) return `+${pct}%`;
  if (pct < 0) return `−${Math.abs(pct)}%`;
  return '0%';
}

/**
 * Popup "chưa đủ thông tin để bắt đầu" — APM-EREF-c, docs/plan-estimate-by-reference.md.
 *
 * Việc thiếu hạn hoặc ước lượng giờ thì không sang "đang làm" / "xong" được.
 * Thay vì một câu báo lỗi, popup cho điền ngay phần còn thiếu:
 *
 * - **Hạn**: ô ngày, nếu người này được đặt hạn (luật đổi lịch của dự án,
 *   APM-TSCH); không thì nói rõ là phải nhờ PM.
 * - **Ước lượng giờ**: chọn một **việc cũ đã làm thật** (tối đa 3, chỉ 1 khi chắc
 *   chắn) — đọc mô tả và checklist của nó — rồi kéo thanh − 0 + xem việc này tốn
 *   hơn / kém bao nhiêu. Giờ + story point tính bằng cùng hàm thuần với server
 *   (`estimateFromReference`), kèm câu lý do. Chênh quá 50% thì nhắc. Không có
 *   mẫu nào thì nhập giờ tay.
 * - **Việc mốc** của neo story point (APM-SPA-c) đứng đầu, mang nhãn vì sao nó là
 *   mốc; thẻ nào cũng hiện số SP, kể cả "chưa có SP" — chọn mốc 1 SP là đọc được
 *   ngay điểm của việc mới.
 *
 * "Lưu và tiếp tục" ghi rồi gọi `onDone` — chỗ gọi chạy lại đúng thao tác bị chặn.
 *
 * ## Chế độ `estimate` — nút "Ước lượng" / "Ước lượng lại" ở drawer (2026-11-02)
 *
 * Cùng hộp, không bị chặn gì: người dùng tự mở để ước lượng (lại) story point.
 * Hai phần: (1) việc ≈1 SP hoặc ≈ giờ neo **của chính người làm trong dự án**
 * (`ownBaseline`) — mốc "1 SP của mình to cỡ nào"; (2) việc tương tự, y như
 * bước ước lượng lúc bắt đầu. Chọn một việc ở phần nào cũng được, rồi kéo tỷ lệ.
 */
export function PlanGateDialog({
  task,
  onDone,
  onCancel,
  mode = 'gate',
}: {
  readonly task: PlanGateTask;
  readonly onDone: () => void;
  readonly onCancel: () => void;
  readonly mode?: 'gate' | 'estimate';
}): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const needsDue = task.missing.includes('dueDate');
  const needsEstimate = task.missing.includes('estimateHours');
  const canSetDue = task.canSchedule !== false;

  const [dueDate, setDueDate] = useState('');
  /** Id việc mẫu đã chọn, `MANUAL` = tự điền số SP, `null` = đang chọn (danh sách mở). */
  const [chosen, setChosen] = useState<string | null>(null);
  const [ratio, setRatio] = useState(0);
  /**
   * SP người chọn sửa lại bằng nút sửa cạnh số đã tính (2026-11-02). `null` = dùng số
   * tính được. Chuỗi vì nó là nội dung ô nhập.
   */
  const [pointsOverride, setPointsOverride] = useState<string | null>(null);
  const [manualHours, setManualHours] = useState(
    task.estimateHours == null ? '' : String(task.estimateHours),
  );
  const [manualPoints, setManualPoints] = useState(
    task.storyPoints == null ? '' : String(task.storyPoints),
  );
  /**
   * Giờ tự đổi theo SP điền tay (theo bậc người được giao) cho tới khi người dùng tự gõ
   * vào ô giờ — người dùng yêu cầu 2026-11-02 ("cũng có thể sửa lại thời gian est").
   * Giờ có sẵn lúc mở hộp được giữ tới khi người dùng đổi SP.
   */
  const [hoursTouched, setHoursTouched] = useState(false);
  /** Giờ đi kèm SP sửa tay ở nhánh có mẫu ("Sửa số"). `null` = không sửa. */
  const [overrideHours, setOverrideHours] = useState<string | null>(null);
  const [overrideHoursTouched, setOverrideHoursTouched] = useState(false);

  const references = useQuery({
    queryKey: ['tasks', task.id, 'estimate-references'],
    queryFn: () => taskFormApi.estimateReferences(task.id),
    enabled: needsEstimate,
  });
  // Người này hay lệch thế nào (APM-EREF-d) — nhắc tự hiệu chỉnh trước khi kéo.
  const calibration = useQuery({
    queryKey: ['me', 'estimate-calibration'],
    queryFn: () => taskFormApi.estimateCalibration(),
    enabled: needsEstimate,
  });
  const estimating = mode === 'estimate';
  const own = estimating ? (references.data?.ownBaseline ?? []) : [];
  const ownIds = new Set(own.map((candidate) => candidate.taskId));
  // Việc đã ở phần 1 thì không lặp lại ở phần 2.
  const candidates = (references.data?.candidates ?? []).filter(
    (candidate) => !ownIds.has(candidate.taskId),
  );
  const shown = [...own, ...candidates];
  /*
    Chọn xong một việc thì danh sách gập lại, chỉ còn việc đã chọn + "Chọn lại", và
    lúc ấy mới hiện tỷ lệ — người dùng yêu cầu 2026-11-02. Không tự chọn sẵn việc
    đầu: tỷ lệ chỉ có nghĩa khi người ta đã nhìn và chọn mẫu.
  */
  const reference: EstimateReferenceCandidate | null =
    chosen === null || chosen === MANUAL
      ? null
      : (shown.find((candidate) => candidate.taskId === chosen) ?? null);
  const picking = shown.length > 0 && (chosen === null || (chosen !== MANUAL && !reference));
  // Hết mẫu thì ô điền số hiện thẳng, không có danh sách để chọn lại.
  const manualMode = chosen === MANUAL || shown.length === 0;
  const storyPointAnchor = references.data?.storyPointAnchor;
  /*
    Giờ cho N SP theo bậc **người được giao** (APM-EREF-i). `assignee === null` = chưa giao
    ai ⇒ không tự tính (server tính lúc giao); `undefined` = API cũ.
  */
  const assignee = references.data?.assignee;
  const autoHours = (value: number): number | null =>
    storyPointAnchor && assignee
      ? hoursFromRates(value, assignee.rates, bucketOf(value * storyPointAnchor.hours))
      : null;
  const hoursHint =
    assignee === null
      ? t(
          estimating
            ? 'taskPlanGate.dialog.hoursAfterAssign'
            : 'taskPlanGate.dialog.hoursNeedAssign',
        )
      : assignee && storyPointAnchor
        ? // Tên bậc chỉ có với người quản lý được giá thuê / tốc độ (2026-11-02).
          assignee.level === undefined
          ? t('taskPlanGate.dialog.hoursAutoNoLevel')
          : t('taskPlanGate.dialog.hoursAuto', {
              level: assignee.level
                ? t(`levelRate.level.${assignee.level}`)
                : t('taskPlanGate.dialog.levelStandard'),
            })
        : null;
  const preview = reference
    ? estimateFromReference(
        { actualHours: reference.actualHours, storyPoints: reference.storyPoints },
        ratio,
      )
    : null;
  /*
    SP của việc mẫu và của việc này — APM-SPA-e. Mẫu có SP thật: SP việc này theo thang
    Fibonacci như server sẽ ghi. Mẫu chưa có SP: số quy đổi theo neo, nhân tỷ lệ, chỉ để
    hiện (không ghi vào việc nào).
  */
  const base = reference ? basePointsOf(reference, storyPointAnchor) : null;
  const currentPoints =
    base === null || !preview
      ? null
      : base.converted
        ? scaleConvertedPoints(base.value, ratio)
        : preview.storyPoints;
  const hasAnchors = candidates.some((candidate) => candidate.anchor);
  const firstPlain = candidates.findIndex((candidate) => !candidate.anchor);
  const tier = references.data?.tier;
  const tierNote = tier
    ? t(
        tier === 'assignee'
          ? 'taskPlanGate.dialog.tierAssignee'
          : tier === 'project'
            ? 'taskPlanGate.dialog.tierProject'
            : 'taskPlanGate.dialog.tierOrg',
      )
    : null;
  // Mở hộp khi việc đã có SP mà chưa có giờ: điền giờ theo bậc ngay khi biết người được
  // giao, không đợi người dùng gõ lại SP. Chỉ chạy khi dữ liệu bậc tới.
  const assigneeRates = assignee === undefined ? undefined : (assignee?.rates.standard ?? null);
  useEffect(() => {
    if (hoursTouched || manualHours.trim() !== '' || !storyPointAnchor || !assignee) return;
    const sp = Number(manualPoints);
    if (manualPoints.trim() === '' || !Number.isFinite(sp)) return;
    setManualHours(
      String(hoursFromRates(sp, assignee.rates, bucketOf(sp * storyPointAnchor.hours))),
    );
  }, [assigneeRates, storyPointAnchor]);
  const manual = Number(manualHours);
  const manualValid = manualHours.trim() !== '' && Number.isFinite(manual) && manual > 0;
  const points = Number(manualPoints);
  const pointsEmpty = manualPoints.trim() === '';
  const pointsValid = !pointsEmpty && Number.isInteger(points) && points >= 0 && points <= 1000;
  // Hộp ước lượng (drawer) là để ra SP ⇒ bắt buộc; cửa chặn chỉ đòi giờ ⇒ tuỳ.
  const pointsOk = pointsValid || (pointsEmpty && !estimating);

  // Esc = Để sau, ở pha capture — cùng lý do với hộp thời gian tối thiểu.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onCancel]);

  const save = useMutation({
    mutationFn: async () => {
      if (needsDue) await taskFormApi.update(task.id, { dueDate });
      if (needsEstimate) {
        await taskFormApi.estimateByReference(
          task.id,
          reference
            ? {
                referenceTaskId: reference.taskId,
                ratioPct: ratio,
                ...(override === null ? {} : { storyPoints: override }),
                ...(override !== null && overrideHoursValid
                  ? { estimateHours: Number(overrideHours) }
                  : {}),
                candidateTaskIds: shown.map((candidate) => candidate.taskId),
              }
            : {
                referenceTaskId: null,
                ratioPct: 0,
                // Thiếu giờ (chưa giao ai, hộp ở drawer) ⇒ server để trống, tính lúc giao.
                ...(manualValid ? { estimateHours: manual } : {}),
                ...(pointsValid ? { storyPoints: points } : {}),
                // Đã hiện mà người dùng chọn tự điền ⇒ ghi lại là đã bỏ qua những mẫu nào.
                candidateTaskIds: shown.map((candidate) => candidate.taskId),
              },
        );
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['board'], exact: false });
      void client.invalidateQueries({ queryKey: ['tasks'], exact: false });
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const dueReady = !needsDue || (canSetDue && dueDate !== '');
  const override = pointsOverride === null ? null : Number(pointsOverride);
  const overrideValid =
    override === null ||
    (pointsOverride!.trim() !== '' &&
      Number.isInteger(override) &&
      override >= 0 &&
      override <= 1000);
  const overrideHoursNumber = overrideHours === null ? NaN : Number(overrideHours);
  const overrideHoursValid =
    overrideHours !== null &&
    overrideHours.trim() !== '' &&
    Number.isFinite(overrideHoursNumber) &&
    overrideHoursNumber > 0;
  /*
    Tự điền: cần giờ — trừ ở hộp của drawer khi việc chưa giao ai và đã có SP: giờ tính
    lúc giao (2026-11-02). Cửa chặn thì luôn cần giờ (không có giờ thì không qua cửa).
  */
  const manualHoursOk = manualValid || (estimating && assignee === null && pointsValid);
  const estimateReady =
    !needsEstimate ||
    (references.isSuccess &&
      ((reference !== null && overrideValid) || (manualMode && manualHoursOk && pointsOk)));
  /*
    Mỗi thông tin còn thiếu một màn, có Tiếp / Quay lại — người dùng yêu cầu 2026-11-02.
    Thứ tự cố định: hạn trước (một ô), ước lượng sau (chọn mẫu + tỷ lệ). Mọi giá trị đã
    điền nằm ở state của component này, nên Quay lại không mất gì; lưu một lần ở bước
    cuối như trước. Thiếu một thứ thì không có bước nào để chuyển — popup như cũ.
  */
  const steps = [
    ...(needsDue ? (['due'] as const) : []),
    ...(needsEstimate ? (['estimate'] as const) : []),
  ];
  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const isLast = stepIndex >= steps.length - 1;
  const stepReady = step === 'due' ? dueReady : estimateReady;
  const ratioIndex = ratioIndexOf(ratio);
  const stepRatio = (delta: number): void =>
    setRatio(
      REFERENCE_RATIO_VALUES[
        Math.min(REFERENCE_RATIO_VALUES.length - 1, Math.max(0, ratioIndex + delta))
      ]!,
    );

  return createPortal(
    <div className="relative z-[60]">
      <div className="fixed inset-0 popup-scrim" onClick={onCancel} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="plan-gate-title"
        aria-describedby="plan-gate-body"
        className="fixed left-1/2 top-1/2 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-3 overflow-y-auto rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-sm on-scrim"
      >
        <div className="flex items-start gap-2">
          <ClipboardCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-rag-amber" />
          <h2 id="plan-gate-title" className="text-base font-semibold">
            {t(estimating ? 'taskPlanGate.dialog.estimateTitle' : 'taskPlanGate.dialog.title')}
          </h2>
        </div>
        <p id="plan-gate-body" className="text-sm">
          {t(estimating ? 'taskPlanGate.dialog.estimateBody' : 'taskPlanGate.dialog.body', {
            title: task.title,
          })}
        </p>

        {steps.length > 1 ? (
          <div className="flex flex-col gap-1.5" data-testid="plan-gate-steps">
            <div className="flex gap-1" aria-hidden>
              {steps.map((name, index) => (
                <span
                  key={name}
                  className={cn(
                    'h-1 flex-1 rounded-full',
                    index <= stepIndex ? 'bg-primary' : 'bg-muted',
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {t('taskPlanGate.dialog.step', {
                current: stepIndex + 1,
                total: steps.length,
                label: t(
                  step === 'due'
                    ? 'taskPlanGate.dialog.dueLabel'
                    : 'taskPlanGate.dialog.estimateLabel',
                ),
              })}
            </p>
          </div>
        ) : null}

        {step === 'due' ? (
          <section className="flex flex-col gap-1.5 rounded-DEFAULT border border-border p-3">
            <p className="text-sm font-medium">{t('taskPlanGate.dialog.dueLabel')}</p>
            {canSetDue ? (
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                min={todayIso()}
                // Popup ở `z-[60]` — lịch phải nổi lên trên nó, không thì bị che.
                popoverClassName="z-[70]"
                aria-label={t('taskPlanGate.dialog.dueLabel')}
              />
            ) : (
              <p className="text-xs text-rag-amber">{t('taskPlanGate.dialog.dueAskPm')}</p>
            )}
          </section>
        ) : null}

        {step === 'estimate' ? (
          <section className="flex flex-col gap-2 rounded-DEFAULT border border-border p-3">
            {estimating ? null : (
              <p className="text-sm font-medium">{t('taskPlanGate.dialog.estimateLabel')}</p>
            )}
            {references.isPending ? (
              <p className="text-xs text-muted-foreground">{t('taskPlanGate.dialog.loading')}</p>
            ) : (
              <>
                {picking ? (
                  <>
                    {estimating ? (
                      <>
                        {/*
                          Không đặt câu tiêu đề "Việc ≈1 SP hoặc ≈4 giờ … đã làm" — người dùng bỏ
                          2026-11-02: 4 giờ chỉ đúng với Junior 1. Nhãn trên từng thẻ đã nói vì sao.
                        */}
                        {own.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {t('taskPlanGate.dialog.ownEmpty')}
                          </p>
                        ) : (
                          <div
                            role="radiogroup"
                            aria-label={t('taskPlanGate.dialog.ownSectionShort')}
                            className="flex flex-col gap-2"
                            data-testid="estimate-own-baseline"
                          >
                            {own.map((candidate) => (
                              <ReferenceCard
                                key={candidate.taskId}
                                candidate={candidate}
                                anchor={storyPointAnchor}
                                labels={ownLabels(candidate, t, i18n.language)}
                                selected={candidate.taskId === reference?.taskId}
                                onSelect={() => setChosen(candidate.taskId)}
                              />
                            ))}
                          </div>
                        )}
                        <p className="border-t border-border pt-2 text-sm font-medium">
                          {t('taskPlanGate.dialog.similarSection')}
                        </p>
                        {candidates.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {t('taskPlanGate.dialog.similarEmpty')}
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    {/*
                      Câu "mẫu lấy ở tầng nào" nói về việc mẫu **thường**. Có việc mốc (APM-SPA)
                      thì nó xuống ngay trước việc thường đầu tiên — đặt trên cùng là nói
                      "việc của người được giao" về hai việc mốc của người khác.
                    */}
                    {candidates.length > 0 && tierNote && !hasAnchors ? (
                      <p className="text-xs font-medium">{tierNote}</p>
                    ) : null}
                    {candidates.length === 0 ? null : (
                      <p className="text-xs text-muted-foreground">
                        {t(
                          references.data?.source === 'recent'
                            ? 'taskPlanGate.dialog.pickRecent'
                            : references.data?.confident
                              ? 'taskPlanGate.dialog.pickOne'
                              : 'taskPlanGate.dialog.pickMany',
                        )}
                      </p>
                    )}
                    {hasAnchors && references.data?.storyPointAnchor ? (
                      <p className="text-xs text-muted-foreground">
                        {t('taskPlanGate.dialog.anchorLine', {
                          level: t(`levelRate.level.${references.data.storyPointAnchor.level}`),
                          hours: references.data.storyPointAnchor.hours,
                        })}
                      </p>
                    ) : null}
                    <div role="radiogroup" className="flex flex-col gap-2">
                      {candidates.map((candidate, index) => (
                        <Fragment key={candidate.taskId}>
                          {hasAnchors && tierNote && index === firstPlain ? (
                            <p className="pt-1 text-xs font-medium">{tierNote}</p>
                          ) : null}
                          <ReferenceCard
                            candidate={candidate}
                            anchor={references.data?.storyPointAnchor}
                            selected={candidate.taskId === reference?.taskId}
                            onSelect={() => setChosen(candidate.taskId)}
                          />
                        </Fragment>
                      ))}
                    </div>

                    <ManualChoice required={estimating} onSelect={() => setChosen(MANUAL)} />
                  </>
                ) : null}

                {shown.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('taskPlanGate.dialog.noReference')}
                  </p>
                ) : null}

                {!picking && shown.length > 0 ? (
                  <div className="flex flex-col gap-1.5" data-testid="estimate-chosen">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        {t(
                          reference
                            ? 'taskPlanGate.dialog.chosenReference'
                            : 'taskPlanGate.dialog.chooseManual',
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => setChosen(null)}
                        className="rounded-control px-1 text-xs font-medium text-primary hover:bg-muted"
                      >
                        {t('taskPlanGate.dialog.repick', { count: shown.length })}
                      </button>
                    </div>
                    {reference ? (
                      <ReferenceCard
                        candidate={reference}
                        anchor={storyPointAnchor}
                        {...(ownIds.has(reference.taskId)
                          ? { labels: ownLabels(reference, t, i18n.language) }
                          : {})}
                        selected
                        onSelect={() => {
                          setChosen(null);
                          setPointsOverride(null);
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}

                {reference ? (
                  <>
                    {calibration.data &&
                    calibration.data.samples >= MIN_CALIBRATION_SAMPLES &&
                    calibration.data.chosenAvgPct !== null &&
                    calibration.data.actualAvgPct !== null ? (
                      <p className="rounded-DEFAULT border border-border p-2 text-xs">
                        {t('taskPlanGate.dialog.calibration', {
                          count: calibration.data.samples,
                          chosen: signed(calibration.data.chosenAvgPct),
                          actual: signed(calibration.data.actualAvgPct),
                        })}
                      </p>
                    ) : null}

                    <div className="flex flex-col gap-1.5">
                      <p className="text-sm">
                        {t('taskPlanGate.dialog.ratioLabel')}{' '}
                        <span className="font-semibold tabular-nums">
                          {ratioLabel(ratio, i18n.language)}
                        </span>
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={t('taskPlanGate.dialog.less')}
                          onClick={() => stepRatio(-1)}
                        >
                          <Minus aria-hidden className="size-4" />
                        </Button>
                        <input
                          type="range"
                          // Chạy theo **chỉ số nấc** (−75% … ×20, thưa dần khi xa 0) —
                          // `aria-valuetext` đọc ra nhãn thật của nấc.
                          min={0}
                          max={REFERENCE_RATIO_VALUES.length - 1}
                          step={1}
                          value={ratioIndex}
                          onChange={(event) =>
                            setRatio(REFERENCE_RATIO_VALUES[Number(event.target.value)] ?? 0)
                          }
                          aria-valuetext={ratioLabel(ratio, i18n.language)}
                          aria-label={t('taskPlanGate.dialog.ratioLabel')}
                          className="h-2 flex-1 accent-primary"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={t('taskPlanGate.dialog.more')}
                          onClick={() => stepRatio(1)}
                        >
                          <Plus aria-hidden className="size-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {REFERENCE_RATIO_STOPS.map((stop) => (
                          <button
                            key={stop}
                            type="button"
                            aria-pressed={ratio === stop}
                            onClick={() => setRatio(stop)}
                            className={cn(
                              'rounded-full border px-2.5 py-0.5 text-xs tabular-nums',
                              ratio === stop
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border hover:bg-muted',
                            )}
                          >
                            {ratioLabel(stop, i18n.language)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/*
                      Chỉ còn số SP tính được và nút sửa — người dùng bỏ câu lý do, cảnh báo
                      và "PM có thể sửa lại" (2026-11-02). Giờ ước lượng vẫn tính và ghi như cũ.
                    */}
                    {reference && preview ? (
                      <ResultPoints
                        computed={currentPoints}
                        converted={base?.converted ?? false}
                        override={pointsOverride}
                        onOverride={(value) => {
                          setPointsOverride(value);
                          if (value === null) {
                            setOverrideHours(null);
                            setOverrideHoursTouched(false);
                            return;
                          }
                          if (overrideHoursTouched) return;
                          const sp = Number(value);
                          const next =
                            value.trim() !== '' && Number.isFinite(sp) ? autoHours(sp) : null;
                          setOverrideHours(
                            next === null ? String(preview?.estimateHours ?? '') : String(next),
                          );
                        }}
                        hours={overrideHours}
                        onHours={(value) => {
                          setOverrideHours(value);
                          setOverrideHoursTouched(true);
                        }}
                        hoursHint={hoursHint}
                      />
                    ) : null}
                  </>
                ) : null}

                {manualMode ? (
                  <ManualFields
                    points={manualPoints}
                    hours={manualHours}
                    pointsRequired={estimating}
                    onPoints={(value) => {
                      setManualPoints(value);
                      if (hoursTouched) return;
                      const sp = Number(value);
                      const next =
                        value.trim() !== '' && Number.isFinite(sp) ? autoHours(sp) : null;
                      if (next !== null) setManualHours(String(next));
                    }}
                    onHours={(value) => {
                      setManualHours(value);
                      setHoursTouched(true);
                    }}
                    hoursHint={hoursHint}
                  />
                ) : null}
              </>
            )}
          </section>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            autoFocus
            className={cn(steps.length > 1 && 'mr-auto')}
          >
            {t(estimating ? 'common.cancel' : 'taskPlanGate.dialog.later')}
          </Button>
          {stepIndex > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
            >
              <ChevronLeft aria-hidden className="size-4" />
              {t('taskPlanGate.dialog.back')}
            </Button>
          ) : null}
          {isLast ? (
            <Button
              type="button"
              disabled={!dueReady || !estimateReady || save.isPending}
              onClick={() => save.mutate()}
            >
              {t(estimating ? 'taskPlanGate.dialog.saveEstimate' : 'taskPlanGate.dialog.save')}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={!stepReady}
              onClick={() => setStepIndex((index) => Math.min(steps.length - 1, index + 1))}
            >
              {t('taskPlanGate.dialog.next')}
              <ChevronRight aria-hidden className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Nhãn "vì sao là mốc" của một việc mẫu — APM-SPA-c. Rỗng = việc mẫu thường. */
export function anchorLabels(
  candidate: Pick<EstimateReferenceCandidate, 'anchor' | 'actualHours' | 'doerLevel'>,
  anchor: StoryPointAnchor | undefined,
  t: TFunction,
  language = 'vi',
): string[] {
  const match = candidate.anchor;
  if (!match) return [];
  const labels: string[] = [];
  if (match.onePoint) {
    labels.push(
      t(match.own ? 'taskPlanGate.dialog.anchorOwn' : 'taskPlanGate.dialog.anchorOnePoint'),
    );
  }
  // Bậc của **người đã làm** (2026-11-02): giờ ≈1 SP khác nhau theo bậc.
  const level = candidate.doerLevel ?? anchor?.level;
  if (match.levelHours && level) {
    labels.push(
      t('taskPlanGate.dialog.anchorLevelHours', {
        level: t(`levelRate.level.${level}`),
        hours: decimal(candidate.actualHours, language),
      }),
    );
  }
  return labels;
}

/**
 * Nhãn của một việc ở phần "của chính người làm" (chế độ `estimate`): giờ thật trong
 * khoảng ≈1 SP **theo bậc của người ấy** (server tính `anchor.levelHours` bằng
 * `oneStoryPointHoursRange` — 2026-11-02: 1 SP là 4 giờ của Junior 1, Senior ít giờ
 * hơn). "1 SP" thì huy hiệu SP của thẻ đã nói — nhãn thứ hai là lặp chữ.
 */
export function ownLabels(
  candidate: Pick<EstimateReferenceCandidate, 'actualHours' | 'anchor' | 'doerLevel'>,
  t: TFunction,
  language = 'vi',
): string[] {
  if (!candidate.anchor?.levelHours) return [];
  return [
    t('taskPlanGate.dialog.ownNearHours', {
      level: candidate.doerLevel
        ? t(`levelRate.level.${candidate.doerLevel}`)
        : t('taskPlanGate.dialog.levelStandard'),
      hours: decimal(candidate.actualHours, language),
    }),
  ];
}

/** Lựa chọn cuối danh sách: không so với việc mẫu nào, điền thẳng số SP. */
function ManualChoice({
  required,
  onSelect,
}: {
  readonly required: boolean;
  readonly onSelect: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid="estimate-manual-choice"
      className="flex items-start gap-2 rounded-DEFAULT border border-dashed border-border p-2.5 text-left hover:bg-muted/50"
    >
      <PencilLine aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{t('taskPlanGate.dialog.chooseManual')}</span>
        <span className="text-xs text-muted-foreground">
          {t(
            required
              ? 'taskPlanGate.dialog.chooseManualHint'
              : 'taskPlanGate.dialog.chooseManualHintGate',
          )}
        </span>
      </span>
    </button>
  );
}

/** Ô SP (kèm mốc nhanh theo thang Fibonacci) và ô giờ của nhánh tự điền. */
/**
 * Số SP của việc này sau khi so với việc mẫu, và nút sửa lại số — người dùng yêu cầu
 * 2026-11-02 ("chỉ để lại số SP tính được và có một nút edit lại số"). Sửa thì hiện ô
 * nhập + mốc nhanh 1…21, điền sẵn số đang thấy (số quy đổi làm tròn lên thang).
 */
function ResultPoints({
  computed,
  converted,
  override,
  onOverride,
  hours,
  onHours,
  hoursHint,
}: {
  readonly computed: number | null;
  readonly converted: boolean;
  readonly override: string | null;
  readonly onOverride: (value: string | null) => void;
  /** Giờ đi kèm khi sửa số — tự đổi theo SP theo bậc người được giao, sửa tay được. */
  readonly hours: string | null;
  readonly onHours: (value: string) => void;
  readonly hoursHint: string | null;
}): ReactNode {
  const { t, i18n } = useTranslation();
  const shown =
    computed === null
      ? t('taskPlanGate.dialog.noSpShort')
      : converted
        ? t('taskPlanGate.dialog.spConverted', { value: decimal(computed, i18n.language) })
        : t('taskPlanGate.dialog.spShort', { count: computed });
  return (
    <div
      role="status"
      data-testid="estimate-result"
      className="flex flex-col gap-2 rounded-DEFAULT bg-muted/60 p-2.5"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {t('taskPlanGate.dialog.resultLabel')}
        </span>
        {override === null ? (
          <span className="text-lg font-semibold tabular-nums" data-testid="estimate-result-sp">
            {shown}
          </span>
        ) : (
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={override}
            onChange={(event) => onOverride(event.target.value)}
            aria-label={t('taskPlanGate.dialog.resultLabel')}
            className="h-8 w-20 rounded-field border border-border bg-background px-2 text-sm"
            autoFocus
          />
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() =>
            onOverride(
              override === null
                ? computed === null
                  ? ''
                  : String(converted ? toStoryPointScale(computed) : computed)
                : null,
            )
          }
        >
          <PencilLine aria-hidden className="size-3.5" />
          {t(
            override === null
              ? 'taskPlanGate.dialog.resultEdit'
              : 'taskPlanGate.dialog.resultUseComputed',
          )}
        </Button>
      </div>
      {override === null ? null : (
        <label className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t('taskPlanGate.dialog.resultHours')}</span>
          <input
            type="number"
            inputMode="decimal"
            min={0.5}
            step={0.5}
            value={hours ?? ''}
            onChange={(event) => onHours(event.target.value)}
            aria-label={t('taskPlanGate.dialog.resultHours')}
            className="h-8 w-20 rounded-field border border-border bg-background px-2 text-sm"
          />
          {hoursHint ? (
            <span className="text-[11px] text-muted-foreground">{hoursHint}</span>
          ) : null}
        </label>
      )}
      {override === null ? null : (
        <div className="flex flex-wrap gap-1.5">
          {STORY_POINT_SCALE.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={override === String(value)}
              onClick={() => onOverride(String(value))}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs tabular-nums',
                override === String(value)
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border hover:bg-muted',
              )}
            >
              {t('taskPlanGate.dialog.spShort', { count: value })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ManualFields({
  points,
  hours,
  pointsRequired,
  onPoints,
  onHours,
  hoursHint,
}: {
  readonly points: string;
  readonly hours: string;
  readonly pointsRequired: boolean;
  readonly onPoints: (value: string) => void;
  readonly onHours: (value: string) => void;
  /** Giờ tự tính theo SP + bậc người được giao (hoặc vì sao chưa tính được). */
  readonly hoursHint?: string | null;
}): ReactNode {
  const { t } = useTranslation();
  const field = 'h-9 w-28 rounded-field border border-border bg-background px-3 text-sm';
  return (
    <div className="flex flex-col gap-2" data-testid="estimate-manual">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span>
            {t('taskPlanGate.dialog.manualPoints')}
            {pointsRequired ? null : (
              <span className="text-muted-foreground"> {t('taskPlanGate.dialog.optional')}</span>
            )}
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={points}
            onChange={(event) => onPoints(event.target.value)}
            className={field}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          {t('taskPlanGate.dialog.manualHours')}
          <input
            type="number"
            inputMode="decimal"
            min={0.5}
            step={0.5}
            value={hours}
            onChange={(event) => onHours(event.target.value)}
            className={field}
          />
        </label>
      </div>
      {hoursHint ? (
        <p className="text-[11px] text-muted-foreground" data-testid="estimate-hours-hint">
          {hoursHint}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {STORY_POINT_SCALE.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={points === String(value)}
            onClick={() => onPoints(String(value))}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs tabular-nums',
              points === String(value)
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:bg-muted',
            )}
          >
            {t('taskPlanGate.dialog.spShort', { count: value })}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Một việc mẫu: đủ để **so** — mô tả, checklist, giờ thật, story point. */
function ReferenceCard({
  candidate,
  anchor,
  labels: labelsOverride,
  selected,
  onSelect,
}: {
  readonly candidate: EstimateReferenceCandidate;
  readonly anchor: StoryPointAnchor | undefined;
  /** Nhãn thay cho nhãn việc mốc (phần "của chính người làm"). */
  readonly labels?: readonly string[];
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation();
  const labels = labelsOverride ?? anchorLabels(candidate, anchor, t, i18n.language);
  const base = basePointsOf(candidate, anchor);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex flex-col gap-1 rounded-DEFAULT border p-2.5 text-left',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
      )}
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium">
          <span className="text-muted-foreground">{candidate.key}</span> {candidate.title}
        </span>
        <span className="flex shrink-0 items-baseline gap-1.5 text-xs tabular-nums">
          {t('taskPlanGate.dialog.actual', { hours: candidate.actualHours })}
          {/*
            Số SP luôn hiện (2026-10-22). Chưa có SP thì hiện số quy đổi theo neo, viền
            đứt + "(quy đổi)" để không lẫn với SP thật (2026-11-02, APM-SPA-e).
          */}
          <span
            data-testid="reference-sp"
            title={
              base?.converted && anchor
                ? t('taskPlanGate.dialog.spConvertedHint', {
                    level: t(`levelRate.level.${anchor.level}`),
                    hours: anchor.hours,
                    doer: candidate.doerLevel
                      ? t(`levelRate.level.${candidate.doerLevel}`)
                      : t('taskPlanGate.dialog.doerUnleveled'),
                  })
                : undefined
            }
            className={cn(
              'rounded-full border px-1.5 py-px text-[11px] font-semibold',
              base === null
                ? 'border-dashed border-border font-normal text-muted-foreground'
                : base.converted
                  ? 'border-dashed border-primary/50 font-medium text-primary'
                  : 'border-primary/40 bg-primary/10 text-primary',
            )}
          >
            {base === null
              ? t('taskPlanGate.dialog.noSpShort')
              : base.converted
                ? t('taskPlanGate.dialog.spConverted', {
                    value: decimal(base.value, i18n.language),
                  })
                : t('taskPlanGate.dialog.spShort', { count: base.value })}
          </span>
        </span>
      </span>
      {labels.length > 0 ? (
        <span className="flex flex-wrap gap-1">
          {labels.map((label) => (
            <span
              key={label}
              className="rounded-full border border-rag-amber/40 bg-rag-amber/10 px-2 py-px text-[11px] font-medium text-rag-amber"
            >
              {label}
            </span>
          ))}
        </span>
      ) : null}
      <span className="text-[11px] text-muted-foreground">{candidate.projectName}</span>
      {candidate.description ? (
        <span className="line-clamp-3 whitespace-pre-line text-xs">{candidate.description}</span>
      ) : null}
      {candidate.checklist.length > 0 ? (
        <ul className="list-inside list-disc text-xs text-muted-foreground">
          {candidate.checklist.slice(0, 5).map((item, index) => (
            <li key={index}>{item}</li>
          ))}
          {candidate.checklist.length > 5 ? (
            <li className="list-none">
              {t('taskPlanGate.dialog.moreItems', { count: candidate.checklist.length - 5 })}
            </li>
          ) : null}
        </ul>
      ) : null}
    </button>
  );
}
